import { useEffect, useState } from 'react';
import api from '@/api';
import { FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-amber-50 text-amber-700',
    active: 'bg-green-50 text-green-700',
    awarded: 'bg-blue-50 text-blue-700',
    closed: 'bg-slate-50 text-slate-500',
  };
  return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${map[status] || 'bg-slate-50 text-slate-600'}`}>{status}</span>;
};

export default function AdminRFQs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/admin/rfqs').then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/rfqs/${id}/status`, { status });
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-rfqs-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">RFQ Management</h1>
        <p className="text-slate-500 text-sm mt-0.5">All customer RFQ requests across DeMaxtore</p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No RFQs yet</p>
            </div>
          ) : (
            <table className="w-full" data-testid="admin-rfqs-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Customer</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Quantity</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Target Price</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map(item => (
                  <tr key={item._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{item.category}</p>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-sm text-slate-600">{item.customer_name}</td>
                    <td className="px-5 py-4 hidden sm:table-cell text-sm text-slate-600">{item.quantity ? `${item.quantity} ${item.unit}` : '—'}</td>
                    <td className="px-5 py-4 hidden lg:table-cell text-sm text-slate-600">{item.target_price ? `${item.currency} ${item.target_price}` : '—'}</td>
                    <td className="px-5 py-4">
                      <Select value={item.status} onValueChange={v => updateStatus(item._id, v)}>
                        <SelectTrigger className="w-32 h-7 text-xs" data-testid={`rfq-status-${item._id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['pending','active','awarded','closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
